// TEMP WORKAROUND: Manually register transformers before any caching happens
// This fixes the "cached.address.toBase58 is not a function" error
// The auto-registration side effects are being tree-shaken away
// Remove once @solomon-labs/caching or @solomon-labs/transformers is fixed
import { FEATURE_FLAGS } from "@solomon-apps/config";
import { createApp, type RouteConfig } from "@solomon-apps/shared";
import {
    BigDecimalTransformer,
    BigIntTransformer,
    PublicKeyTransformer,
    TransformerRegistry,
    Uint8ArrayTransformer
} from "@solomon-labs/transformers";

// Force manual registration since side effects are tree-shaken
TransformerRegistry.register(PublicKeyTransformer);
TransformerRegistry.register(BigIntTransformer);
TransformerRegistry.register(BigDecimalTransformer);
TransformerRegistry.register(Uint8ArrayTransformer);

import { V3Layout } from "./components/V3Layout";
import { PerksPage } from "./pages/Perks/PerksPage";
import { PerksPositionsPage } from "./pages/Perks/PerksPositionsPage";
import { DistributionsPageV3 } from "./pages/Stats/DistributionsPageV3";
import { StatsV4Page } from "./pages/StatsV4/StatsV4Page";
import { USDvHomeRoute } from "./pages/USDv/USDvHomeRoute";
import { VenuesPage } from "./pages/Venues/VenuesPage";

/**
 * Solomon Main Website App entry point
 */
void (async (): Promise<void> => {
    let showcaseRoutes: RouteConfig[] = [];
    // The showcase pages are for development and QA only. The check must stay
    // a literal comparison: esbuild folds it to `false` in a production build
    // and removes the block, and with it this import and every module that
    // only it reaches. `yarn check:production-sim main` proves it.
    if (__BUILD_MODE__ !== "production") {
        showcaseRoutes = (await import("./pages/Showcase/showcaseRoutes"))
            .SHOWCASE_ROUTES;
    }

    await createApp({
        appName: "main",
        routes: [
            // ─── v3 canonical routes (no v2 Layout wrapper) ─────────
            // These are the main site. Each page renders its own
            // NavigationBar via the v3 atoms.
            {
                key: "stats",
                path: "/stats",
                component: StatsV4Page
            },
            {
                key: "stats-distributions",
                path: "/stats/distributions",
                component: DistributionsPageV3
            },
            // Each tab is addressable: /stats/reserves, /stats/rewards,
            // /stats/security. React Router ranks the static
            // "/stats/distributions" above this dynamic segment, so the
            // distributions page still wins its own path.
            {
                key: "stats-tab",
                path: "/stats/:tab",
                component: StatsV4Page
            },
            // Standard pages — parked at /wip/* while the product is on
            // hold. Code stays wired (no dead imports); pages are still
            // reachable for internal review but won't be crawled at their
            // canonical paths. Move back to /standard when ready to ship.
            {
                key: "standard",
                path: "/wip/standard",
                component: PerksPage
            },
            {
                key: "standard-positions",
                path: "/wip/standard/positions",
                component: PerksPositionsPage
            },
            ...showcaseRoutes,
            // Root: wallet-aware wrapper that picks between the USDv
            // landing page (disconnected / zero balance) and the USDv
            // positions dashboard (connected with USDv/sUSDv balance or
            // pending unstakes). Mirrors the v2 `Home.tsx` decision logic.
            {
                key: "home",
                path: "/",
                component: USDvHomeRoute
            },
            // /venues — DRAFT coverage registry. Lists the surfaces an
            // Active Program covers, so USDv keeps earning Solomon
            // rewards while it sits there. Runs on mock data until
            // yaas-api exposes a public read of the Surface catalog.
            //
            // Gated on `FEATURE_FLAGS.venues`: defaults omit the route
            // for retail launch (FEATURE_DISCOVERY can override), so direct URL
            // visits fall through to the catch-all NotFoundPage. The
            // nav item and the in-page ghost row read the same flag,
            // so the surface is never advertised from a build that
            // cannot serve it.
            ...(FEATURE_FLAGS.venues
                ? [
                      {
                          key: "venues",
                          path: "/venues",
                          component: VenuesPage
                      }
                  ]
                : [])
            // `/yaas` retired — the YaaS surface folded into USDv.
            // `USDvHomeRoute` delegates `/` to `RetailYaasLandingPage`
            // for every position-aware state, so the standalone route
            // served nothing `/` did not. `?state=` overrides work the
            // same way at `/`. `/wip/yaas-*` stays registered in all
            // environments — those are internal references.
        ],
        components: {
            v3Layout: V3Layout
        }
    });
})();
